import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import Scrollbars from "react-custom-scrollbars-2";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllPatients, getPatientPrescription } from ".././PharmacySales/SalesService";

import "./NewSales.scss";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { useCurrency } from "../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { isHospitalConnected as isPharmacyHospitalConnected } from "../../utils/pharmacyConnection";

import CustomerSelector from "./ReturnSales/components/CustomerSelector";
import CartTable from "./ReturnSales/components/CartTable";
import CartSummary from "./ReturnSales/components/CartSummary";
import PaymentModal from "./ReturnSales/components/PaymentModal";
import InvoiceModal from "./ReturnSales/components/InvoiceModal";

import { useReturnCart } from "./ReturnSales/hooks/useReturnCart";
import { useReturnPaymentManager } from "./ReturnSales/hooks/useReturnPaymentManager";
import { mapPatientsToOptions, buildPatientOptionLabel, buildPatientFromSale, isRegisteredCustomerType } from "./ReturnSales/utils/patientUtils";

import PrescriptionSummary from "./ReturnSales/components/PrescriptionSummary";
import { calculateItemTax } from "./NewSales/utils/taxCalculations";
import {
    getActivePosTaxType,
    getCartItemForTaxCalculation,
    shouldShowPosTaxUi,
} from "../PharmacyManageTax/taxConfigUtils";

const ReturnSales = () => {
    const location = useLocation();
    const { patientId, patientName, stateCustomerType } = location.state || {};

    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const { pharmacyPOS } = usePharmacy();
    const { getCurrencySymbol, currency, convertCurrency } = useCurrency();
    const disableScrollProps = useDisableScrollOnFocus();

    const isHospitalConnected = isPharmacyHospitalConnected(pharmacyPOS);
    const patientSelectRef = useRef(null);
    const saleNumberInputRef = useRef(null);

    const [contentHeight, setContentHeight] = useState(window.innerHeight - 166);
    const [discountType, setDiscountType] = useState("Percentage");
    const [discountValue, setDiscountValue] = useState(0);
    const [transportCost] = useState(0);
    const [customerType, setCustomerType] = useState("walking");
    const [walkingCustomerName, setWalkingCustomerName] = useState("");
    const [saleNumberValue, setSaleNumberValue] = useState("");

    const [selectedPatient, setSelectedPatient] = useState(null);
    const [customerError, setCustomerError] = useState(false);
    const [exchangeRate] = useState(0);
    const [recentPurchases, setRecentPurchases] = useState([]);
    const [parentSalesId, setParentSalesId] = useState(null);
    const [saleSearchMode, setSaleSearchMode] = useState(false);

    // Tax system states
    const [pharmacyStateCode] = useState(pharmacyPOS?.stateCode || "");
    const [patientStateCode, setPatientStateCode] = useState("");
    const posTaxType = useMemo(
        () => getActivePosTaxType(pharmacyPOS, "gst"),
        [pharmacyPOS]
    );
    const [itemTaxes, setItemTaxes] = useState({});

    const {
        cart,
        addProductToCart,
        handleBatchChange,
        updateItemField,
        handleQuantityChange,
        removeItem,
        resetCart,
        setCart,
        handleTaxChange,
    } = useReturnCart(t);

    const showReturnTax = useMemo(
        () => shouldShowPosTaxUi(pharmacyPOS, cart),
        [pharmacyPOS, cart]
    );

    // Calculate item taxes whenever cart or state codes change
    useEffect(() => {
        if (cart.length > 0) {
            const newItemTaxes = {};
            cart.forEach(item => {
                const taxInfo = calculateItemTax(
                    getCartItemForTaxCalculation(pharmacyPOS, item),
                    pharmacyStateCode,
                    patientStateCode,
                    posTaxType
                );
                newItemTaxes[`${item._id}-${item.selectedBatchId}`] = taxInfo;
            });
            setItemTaxes(newItemTaxes);
        } else {
            setItemTaxes({});
        }
    }, [cart, pharmacyStateCode, patientStateCode, posTaxType, pharmacyPOS]);

    const {
        paymentData,
        setPaymentData,
        showPaymentModal,
        openPaymentModal,
        closePaymentModal,
        handlePaymentSave,
        errors,
        showInvoice,
        invoiceData,
        closeInvoice,
        resetPaymentState,
        totals,
    } = useReturnPaymentManager({
        t,
        cart,
        itemTaxes,
        discountType,
        discountValue,
        transportCost,
        customerType,
        selectedPatient,
        exchangeRate,
        pharmacyPOS,
        getCurrencySymbol,
        setLoading,
        parentSalesId,
        convertCurrency,
        currency,
        posTaxType,
        pharmacyStateCode,
        patientStateCode
    });

    const [recentPrescriptions, setRecentPrescriptions] = useState([]);
    const [showPrescriptionsCard, setShowPrescriptionsCard] = useState(false);

    // ✅ Load patients with clinic_id only for hospital_patient type
    const loadPatients = useCallback(
        async (inputValue) => {
            if (!inputValue || inputValue.length < 3) return [];

            try {
                const payload = {
                    search: inputValue,
                };

                // Only add clinic_id if hospital connected AND customer type is hospital_patient
                if (isHospitalConnected && customerType === "hospital_patient") {
                    payload.clinic_id = pharmacyPOS.hospital_id;
                    console.log("🏥 Loading hospital patients with clinic_id:", pharmacyPOS.hospital_id);
                } else {
                    console.log("🏪 Loading pharmacy patients without clinic_id");
                }

                const response = await getAllPatients(payload);
                const patients = response.data?.data || [];

                return mapPatientsToOptions(patients, isHospitalConnected, customerType);
            } catch (error) {
                console.error("Error searching patients:", error);
                return [];
            }
        },
        [isHospitalConnected, customerType, pharmacyPOS?.hospital_id]
    );

    const applySaleCustomerContext = useCallback(
        (sale) => {
            if (!sale) return;

            const saleCustomerType = sale.customerType || "walking";
            const patient = buildPatientFromSale(sale);

            if (sale.patientName) {
                setWalkingCustomerName(sale.patientName);
                setPaymentData((prev) => ({ ...prev, patientName: sale.patientName }));
            }

            if (isRegisteredCustomerType(saleCustomerType) && patient?._id) {
                setCustomerType(saleCustomerType);
                setSelectedPatient(patient);
                setPatientStateCode(patient.stateCode || "");
                setSaleSearchMode(false);
                setCustomerError(false);

                const displayName = buildPatientOptionLabel(patient, isHospitalConnected);
                setPaymentData((prev) => ({ ...prev, patientName: displayName }));

                if (patientSelectRef.current) {
                    patientSelectRef.current.setValue({
                        value: patient._id,
                        label: displayName,
                        data: patient,
                    });
                }
                return;
            }

            // Walk-in sale, or registered sale without a resolvable patient id — keep walk-in flow.
            setCustomerType("walking");
            setSelectedPatient(null);
            setPatientStateCode("");
        },
        [isHospitalConnected, setPaymentData]
    );

    // ✅ Select a sale/return item
    const handleSelectSale = useCallback(
        (sale) => {
            setParentSalesId(sale._id);
            resetCart();
            applySaleCustomerContext(sale);
            // console.log(sale);

            sale.salesHistory.forEach((item) => {
                addProductToCart({
                    _id: item.itemId,
                    stockId: item.stockId,
                    batchNumber: item.batchNumber,
                    brandName: item.brandName || item.medicineName,
                    isOTC: item.isOTC,
                    isUnani: item.isUnani,
                    saltName: item.medicineName || item.saltName || "",
                    manufacturer: item.manufacturer || "",
                    quantity: item.medicineQunatity || 0,
                    salePrice: item.unitPricePerMedicine || 0,
                    selectedBatchId: item.stockId,
                    batchList: [{ _id: item.stockId, batchNumber: item.batchNumber }],
                    isReturn: item.isReturn,
                    // Preserve original tax information
                    taxPercentage: parseFloat(item.taxPercentage) || 0,
                    CGSTPercentage: parseFloat(item.CGSTPercentage) || 0,
                    SGSTPercentage: parseFloat(item.SGSTPercentage) || 0,
                    IGSTPercentage: parseFloat(item.IGSTPercentage) || 0,
                    isIGST: item.isIGST || false,
                    CGST: parseFloat(item.CGST) || 0,
                    SGST: parseFloat(item.SGST) || 0,
                    IGST: parseFloat(item.IGST) || 0,
                    tax: parseFloat(item.tax) || 0
                });
            });
            // set discount value from sale
            setDiscountValue(sale.discount);
            setDiscountType(sale.discountType === "percentage" ? "Percentage" : "Fixed");

            // console.log("✅ Sale selected and items added to cart:", sale._id);
        },
        [addProductToCart, resetCart, applySaleCustomerContext]
    );

    // ✅ Search by sale number with clinic_id support
    const handleSearchBySaleNumber = useCallback(
        async (saleNumber) => {
            if (!saleNumber || saleNumber.trim() === "") {
                toast.info(t("Please enter a sale/invoice number"));
                return;
            }

            try {
                setLoading(true);
                const payload = {
                    saleNumber: saleNumber.trim(),
                };

                // Only add clinic_id if hospital connected AND customer type is hospital_patient
                if (isHospitalConnected && customerType === "hospital_patient") {
                    payload.clinic_id = pharmacyPOS.hospital_id;
                    console.log("🏥 Searching sale with clinic_id:", pharmacyPOS.hospital_id);
                } else {
                    console.log("🏪 Searching sale without clinic_id");
                }

                const response = await getPatientPrescription(payload);

                if (response.data.status === 'success') {
                    const sales = response.data.data.lastFewSales || [];

                    if (sales.length === 0) {
                        toast.warning(t("Sale not found"));
                        setRecentPurchases([]);
                        return;
                    }

                    // Get the first (most recent) sale
                    const sale = sales[0];

                    applySaleCustomerContext(sale);

                    // Set recent purchases to show all matching sales
                    setRecentPurchases(sales);
                    setShowPrescriptionsCard(true);

                    console.log("✅ Sale found:", sale._id);
                    // toast.success(t("Sale found successfully"));
                } else {
                    toast.warning(t("Sale not found"));
                    setRecentPurchases([]);
                }
            } catch (error) {
                console.error("Error searching sale:", error);
                toast.warning(t("Error searching sale. Please check the number and try again."));
                setRecentPurchases([]);
            } finally {
                setLoading(false);
            }
        },
        [isHospitalConnected, customerType, pharmacyPOS?.hospital_id, setLoading, t, applySaleCustomerContext]
    );

    // ✅ Load recent purchases with clinic_id support
    const loadRecentPurchases = useCallback(
        async (patientId) => {
            try {
                setLoading(true);
                const body = {
                    patient_id: patientId,
                    pageNo: 1,
                    pageSize: 10,
                };

                // Only add clinic_id if hospital connected AND customer type is hospital_patient
                // if (isHospitalConnected && customerType === "hospital_patient") {
                //     body.clinic_id = pharmacyPOS.hospital_id;
                //     body.isHospitalPatient = true;
                //     console.log("🏥 Loading hospital patient purchases with clinic_id:", pharmacyPOS.hospital_id);
                // } else {
                //     console.log("🏪 Loading pharmacy patient purchases without clinic_id");
                // }

                const response = await getPatientPrescription(body);
                const sales = response.data?.data?.lastFewSales || [];
                setRecentPurchases(sales);
                // console.log("✅ Recent purchases ", sales);
            } catch (err) {
                console.error("Error loading sales:", err);
                setRecentPurchases([]);
            } finally {
                setLoading(false);
            }
        },
        [isHospitalConnected, customerType, pharmacyPOS?.hospital_id, setLoading]
    );

    const handleClosePrescriptionCard = useCallback(() => {
        setShowPrescriptionsCard(false);
        setRecentPurchases([]);
    }, []);

    // ✅ Handle patient selection with clinic_id support
    const handlePatientChange = useCallback(
        async (selectedOption) => {
            const raw = selectedOption ? selectedOption.data : null;
            // Normalize patient object with all name fields
            const patient = raw
                ? {
                    ...raw,
                    full_name: raw.full_name || raw.patient_name || raw.name || "",
                    patient_name: raw.patient_name || raw.full_name || raw.name || "",
                    name: raw.name || raw.full_name || raw.patient_name || "",
                    stateCode: raw.stateCode || ""
                }
                : null;

            setSelectedPatient(patient);
            setPatientStateCode(patient?.stateCode || "");
            setCustomerError(false);
            setSaleSearchMode(false);

            if (patient) {
                const displayName = buildPatientOptionLabel(patient, isHospitalConnected);
                setPaymentData((prev) => ({ ...prev, patientName: displayName }));
                await loadRecentPurchases(patient._id);
                setShowPrescriptionsCard(true);
                console.log("✅ Patient selected:", patient._id);
            } else {
                setPaymentData((prev) => ({ ...prev, patientName: "" }));
                setRecentPurchases([]);
                setShowPrescriptionsCard(false);
            }
        },
        [isHospitalConnected, loadRecentPurchases, setPaymentData]
    );

    // ✅ Handle customer type change with proper cleanup
    const handleCustomerTypeChange = useCallback(
        (type) => {
            if (type === customerType) return;

            console.log("🔄 Customer type changing to:", type);
            setCustomerType(type);
            setCustomerError(false);
            setSaleNumberValue("");
            resetCart();
            setRecentPurchases([]);
            setShowPrescriptionsCard(false);

            if (type === "walking") {
                // Switching to walk-in
                setSelectedPatient(null);
                setPatientStateCode("");
                setPaymentData((prev) => ({ ...prev, patientName: walkingCustomerName }));
                if (patientSelectRef.current) {
                    patientSelectRef.current.setValue(null);
                }
            } else {
                // Switching to existing customer or hospital patient
                setWalkingCustomerName("");
                setSelectedPatient(null);
                setPatientStateCode("");
                setPaymentData((prev) => ({ ...prev, patientName: "" }));
                if (patientSelectRef.current) {
                    patientSelectRef.current.setValue(null);
                }
            }
        },
        [customerType, walkingCustomerName, resetCart, setPaymentData]
    );

    const handleWalkingNameChange = useCallback(
        (value) => {
            setWalkingCustomerName(value);
            setPaymentData((prev) => ({ ...prev, patientName: value }));
            setCustomerError(false);
            setSaleSearchMode(false);
        },
        [setPaymentData]
    );

    const handleOpenPaymentModal = useCallback(() => {
        setCustomerError(false);

        if (cart.length === 0) {
            toast.info(t("Please select at least one product before saving."));
            return;
        }

        const netAmount = totals.netTotal;

        if (customerType === "inpatient" || customerType === "hospital_patient") {
            if (!selectedPatient) {
                setCustomerError(true);
                Swal.fire({
                    icon: "info",
                    text: t("Please select a patient first"),
                    confirmButtonText: t("OK"),
                });
                return;
            }
            const name = buildPatientOptionLabel(selectedPatient, isHospitalConnected);
            openPaymentModal({ customerName: name, amount: netAmount });
        } else {
            if (paymentData.patientName && paymentData.patientName.trim() !== "") {
                openPaymentModal({ customerName: paymentData.patientName, amount: netAmount });
            } else {
                setCustomerError(true);
                Swal.fire({
                    icon: "info",
                    text: t("Please enter walk-in customer name or search by sale number"),
                    confirmButtonText: t("OK"),
                });
            }
        }
    }, [
        cart.length,
        customerType,
        openPaymentModal,
        paymentData.patientName,
        selectedPatient,
        t,
        totals.netTotal,
        isHospitalConnected
    ]);

    const resetSale = useCallback(() => {
        resetCart();
        resetPaymentState();
        setDiscountType("Percentage");
        setDiscountValue(0);
        setCustomerType("walking");
        setWalkingCustomerName("");
        setSelectedPatient(null);
        setPatientStateCode("");
        setCustomerError(false);
        handleClosePrescriptionCard();
        setRecentPrescriptions([]);
        setShowPrescriptionsCard(false);
        setItemTaxes({});
        setSaleSearchMode(false);
        setSaleNumberValue("");
        if (patientSelectRef.current) {
            patientSelectRef.current.setValue(null);
        }
    }, [
        resetCart,
        resetPaymentState,
        handleClosePrescriptionCard,
    ]);

    const printInvoice = useCallback(() => {
        const invoiceContent = document?.getElementById("invoice-content");
        if (!invoiceContent) return;
        const printWindow = window.open("", "", "width=400,height=600");
        if (!printWindow) return;
        printWindow.document.write(`
        <html>
            <head>
            <title>Invoice</title>
            <style>
                @page { size: 80mm auto; margin: 5mm; }
                body { font-family: Arial, sans-serif; font-size: 12px; width: 80mm; margin: 0 auto; }
                .text-center { text-align: center; }
                .text-end { text-align: right; }
                .bold { font-weight: bold; }
                .dotted { border-bottom: 1px dotted #000; margin: 4px 0; }
                table { width: 100%; border-collapse: collapse; font-size: 12px; }
                td { padding: 2px 0; vertical-align: top; }
                .section { margin-top: 6px; margin-bottom: 6px; }
                .thankyou { margin-top: 12px; font-size: 12px; text-align: center; }
            </style>
            </head>
            <body>${invoiceContent.innerHTML}</body>
        </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 400);
    }, []);

    useEffect(() => {
        const measureViewport = () => {
            const header = document.querySelector(".page-header");
            const headerHeight = header?.offsetHeight ?? 80;
            setContentHeight(window.innerHeight - headerHeight - 8);
        };
        window.addEventListener("resize", measureViewport);
        measureViewport();
        return () => window.removeEventListener("resize", measureViewport);
    }, []);

    const { cartScrollHeight, summaryScrollHeight } = useMemo(() => {
        const actionBar = 58;
        const chrome = 96;
        const customer = 70;
        const summaryDiscount = 46;
        const hasTaxSummary = showReturnTax && totals.tax > 0 && cart.length > 0;
        const taxSummaryExtra = hasTaxSummary ? 120 : 0;
        const available = Math.max(
            200,
            contentHeight - actionBar - chrome - customer - summaryDiscount - taxSummaryExtra
        );
        const cartH = Math.max(100, Math.floor(available * 0.52));
        const summaryH = Math.max(100, available - cartH);
        return { cartScrollHeight: cartH, summaryScrollHeight: summaryH };
    }, [contentHeight, totals.tax, cart.length, showReturnTax]);

    useEffect(() => {
        if (patientId && patientName) {
            const patient = {
                _id: patientId,
                full_name: patientName,
                patient_name: patientName,
                name: patientName,
                stateCode: ""
            };
            setSelectedPatient(patient);
            setCustomerType(stateCustomerType);
            setPaymentData((prev) => ({ ...prev, patientName }));
            if (patientSelectRef.current) {
                patientSelectRef.current.setValue({
                    value: patient._id,
                    label: patient.full_name,
                    data: patient,
                });
            }
            loadRecentPurchases(patientId);
        }
    }, [patientId, patientName, setPaymentData, loadRecentPurchases]);

    const handleSubmitReturn = useCallback(async () => {
        const success = await handlePaymentSave();
        if (success) {
            // Hook handles invoice modal
        }
    }, [handlePaymentSave]);

    const updateReturnQuantity = useCallback(
        (_id, value, maxValue) => {
            const val = Math.max(0, Math.min(Number(value), maxValue));

            setCart(prev =>
                prev.map(item =>
                    item._id === _id
                        ? { ...item, returnQuantity: val }
                        : item
                )
            );
        },
        [setCart]
    );

    return (
        <Fragment>
            <div className="new-sales return-sales-theme pos-viewport" style={{ height: `${contentHeight}px` }}>
                <div className="content-wrapper h-100">
                    <Row className="pt-1 g-3 pos-content h-100 mx-0">
                        <Col sm={12} md={8} className="h-100">
                            <div className="pos-left-column">
                                <Card className="pos-cart-card">
                                    <Card.Body className="p-2">
                                        <CustomerSelector
                                            t={t}
                                            isHospitalConnected={isHospitalConnected}
                                            customerType={customerType}
                                            onCustomerTypeChange={handleCustomerTypeChange}
                                            walkingCustomerName={walkingCustomerName}
                                            onWalkingNameChange={handleWalkingNameChange}
                                            customerError={customerError}
                                            patientSelectRef={patientSelectRef}
                                            loadPatients={loadPatients}
                                            onPatientChange={handlePatientChange}
                                            disableScrollProps={disableScrollProps}
                                            onSearchBySaleNumber={handleSearchBySaleNumber}
                                            saleSearchMode={saleSearchMode}
                                            onSaleSearchModeChange={setSaleSearchMode}
                                            saleNumberInputRef={saleNumberInputRef}
                                            saleNumberValue={saleNumberValue}
                                            setSaleNumberValue={setSaleNumberValue}
                                        />
                                        <Scrollbars
                                            autoHide={false}
                                            hideTracksWhenNotNeeded
                                            style={{ height: `${cartScrollHeight}px` }}
                                            universal
                                        >
                                            <CartTable
                                                posCompact={true}
                                                showReturnTax={showReturnTax}
                                                t={t}
                                                cart={cart}
                                                getCurrencySymbol={getCurrencySymbol}
                                                onBatchChange={handleBatchChange}
                                                onPriceChange={updateItemField}
                                                onQuantityChange={handleQuantityChange}
                                                onTaxChange={handleTaxChange}
                                                onRemoveItem={removeItem}
                                                disableScrollProps={disableScrollProps}
                                                updateReturnQuantity={updateReturnQuantity}
                                                itemTaxes={itemTaxes}
                                                posTaxType={posTaxType}
                                                pharmacyStateCode={pharmacyStateCode}
                                                patientStateCode={patientStateCode}
                                                totals={totals}
                                            />
                                        </Scrollbars>
                                    </Card.Body>
                                </Card>
                                <Card className="pos-summary-card mb-0">
                                    <Card.Body className="pos-summary-card__body p-2">
                                        <CartSummary
                                            scrollHeight={summaryScrollHeight}
                                            showReturnTax={showReturnTax}
                                            t={t}
                                            discountType={discountType}
                                            onDiscountTypeChange={setDiscountType}
                                            discountValue={discountValue}
                                            onDiscountValueChange={(value) => setDiscountValue(Number(value) || 0)}
                                            totals={totals}
                                            getCurrencySymbol={getCurrencySymbol}
                                            disableScrollProps={disableScrollProps}
                                            posTaxType={posTaxType}
                                            pharmacyStateCode={pharmacyStateCode}
                                            patientStateCode={patientStateCode}
                                            cart={cart}
                                            itemTaxes={itemTaxes}
                                        />
                                    </Card.Body>
                                </Card>

                                <div className="pos-action-footer">
                                    <div className="d-flex w-100 justify-content-end gap-2 py-2 px-4 border-top mb-0">
                                        <Button
                                            variant="danger"
                                            size="md"
                                            style={{ fontWeight: "bold" }}
                                            onClick={handleOpenPaymentModal}
                                        >
                                            {t("Process Return")}
                                        </Button>
                                        <Button variant="secondary" size="md" onClick={resetSale}>
                                            {t("Reset")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col sm={12} md={4} className="h-100 pos-right-column">
                            <div className="pos-right-column__inner" style={{ height: `${contentHeight}px` }}>
                                {showPrescriptionsCard && (
                                    <PrescriptionSummary
                                        t={t}
                                        selectedPatient={selectedPatient}
                                        recentPrescriptions={recentPurchases}
                                        onClose={handleClosePrescriptionCard}
                                        onSelectPrescription={handleSelectSale}
                                        isHospitalConnected={isHospitalConnected}
                                        isWalkingCustomer={customerType === "walking" && saleSearchMode}
                                        walkingCustomerName={walkingCustomerName}
                                    />
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
            <PaymentModal
                t={t}
                show={showPaymentModal}
                onHide={closePaymentModal}
                paymentData={paymentData}
                onPaymentDataChange={(patch) => setPaymentData((prev) => ({ ...prev, ...patch }))}
                errors={errors}
                handleSave={handleSubmitReturn}
                disableScrollProps={disableScrollProps}
                totals={totals}
                getCurrencySymbol={getCurrencySymbol}
                convertCurrency={convertCurrency}
                defaultCurrency={currency}
            />
            <InvoiceModal
                t={t}
                show={showInvoice}
                invoiceData={invoiceData}
                onHide={closeInvoice}
                onClose={() => {
                    closeInvoice();
                    resetSale();
                }}
                onPrint={printInvoice}
                pharmacyData={pharmacyPOS}
            />
        </Fragment>
    );
};

export default ReturnSales;